import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api, { getClients } from '../services/api';

const STATUS_BADGE = { Draft:'badge-muted', Signed:'badge-info', Closed:'badge-green' };
const EMPTY = { client_id:'', contract_no:'', subject:'', owner_name:'', items:'', closure_date:'', notes:'', status:'Draft' };

function printClosure(c, clientData) {
  const client = clientData?.find(cl=>String(cl.id)===String(c.client_id));
  const items = (c.items||'').split('\n').filter(i=>i.trim()).map(i=>'<li style="margin-bottom:8px;line-height:1.8">'+i+'</li>').join('');
  const win = window.open('', '_blank');
  win.document.write(`<!DOCTYPE html><html><head>
  <meta charset="UTF-8"/>
  <title>مستند استلام مشروع</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@300;400;500;600;700;800&display=swap');
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:'Cairo',Arial,sans-serif;background:#fff;color:#111;font-size:14px;direction:rtl}
    .page{max-width:800px;margin:0 auto;padding:50px 40px;min-height:100vh}
    .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:30px;padding-bottom:20px;border-bottom:3px solid #00b368}
    .logo-area{text-align:right}
    .company-name{font-size:22px;font-weight:800;color:#00b368;letter-spacing:-0.5px}
    .company-sub{font-size:11px;color:#888;margin-top:3px}
    .logo-img{width:70px;height:70px;border-radius:12px;object-fit:cover}
    .doc-title-area{text-align:center;flex:1;padding:0 20px}
    .doc-title{font-size:20px;font-weight:800;color:#1a2332;margin-bottom:6px}
    .doc-subtitle{font-size:13px;color:#666}
    .badge{display:inline-block;padding:4px 14px;border-radius:20px;font-size:11px;font-weight:700;background:#dcfce7;color:#166534;margin-top:6px}

    .info-section{background:linear-gradient(135deg,#f0fdf7,#e8f8f1);border:1.5px solid #86efac;border-radius:12px;padding:18px 24px;margin-bottom:24px}
    .info-row{display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;padding-bottom:8px;border-bottom:1px solid rgba(0,179,104,0.15)}
    .info-row:last-child{margin-bottom:0;padding-bottom:0;border-bottom:none}
    .info-label{font-size:12px;color:#00b368;font-weight:700;min-width:120px}
    .info-value{font-size:13px;font-weight:600;color:#1a2332;flex:1;text-align:right}

    .main-text{background:#fff;border:1px solid #e8ecf0;border-radius:12px;padding:24px;margin-bottom:24px;line-height:2.2;font-size:14px;color:#374151;text-align:justify}
    .main-text strong{color:#00b368}

    .items-section{margin-bottom:24px;background:#fafbfc;border:1px solid #e8ecf0;border-radius:12px;padding:20px}
    .items-title{font-size:13px;font-weight:700;color:#00b368;margin-bottom:12px;text-transform:uppercase;letter-spacing:.5px}
    .items-list{list-style:none;padding:0}
    .items-list li{padding:8px 16px;border-bottom:1px solid #f0f0f0;display:flex;align-items:center;gap:8px}
    .items-list li:last-child{border-bottom:none}
    .items-list li::before{content:"✓";color:#00b368;font-weight:700;font-size:14px}

    .terms{background:#fff8f0;border:1px solid #fed7aa;border-radius:10px;padding:16px 20px;font-size:12px;color:#92400e;line-height:2;margin-bottom:30px;text-align:center;font-weight:500}

    .sig-section{margin-top:40px;display:flex;justify-content:center}
    .sig-box{text-align:center;width:280px}
    .sig-title{font-size:15px;font-weight:700;color:#1a2332;margin-bottom:6px}
    .sig-name-label{font-size:12px;color:#888;margin-bottom:4px}
    .sig-name-value{font-size:14px;font-weight:600;color:#1a2332;margin-bottom:30px}
    .sig-line{border-top:1.5px solid #334155;margin-top:10px;padding-top:10px;font-size:11px;color:#888}

    .footer{margin-top:40px;padding-top:20px;border-top:2px solid #e8ecf0;display:flex;justify-content:space-between;align-items:center}
    .footer-company{font-size:11px;color:#aaa;line-height:1.8}
    .footer-date{font-size:11px;color:#aaa;text-align:left}

    @media print{body{print-color-adjust:exact;-webkit-print-color-adjust:exact}.page{padding:30px}}
  </style></head><body><img src="https://teknulugy.online/logo.svg" style="height:50px;display:block;margin:0 auto 10px auto">
  <div class="page">

    <!-- Header -->
    <div class="header">
      <div style="text-align:left">
        <div style="font-size:11px;color:#888;margin-bottom:4px">التاريخ: ${c.closure_date||new Date().toLocaleDateString('ar-KW')}</div>
        <span class="badge">${c.status}</span>
      </div>
      <div class="doc-title-area">
        <div class="doc-title">مستند استلام مشروع</div>
        <div class="doc-subtitle">Project Closure Document</div>
      </div>
      <div class="logo-area">
        <div class="company-name">TEKNULUGY</div>
        <div class="company-sub">تكنولوجي المتكاملة</div>
        <div class="company-sub" style="color:#00b368;margin-top:2px">لتصميم وبرمجة البرمجيات الخاصة</div>
      </div>
    </div>

    <!-- Info Section -->
    <div class="info-section">
      <div class="info-row">
        <span class="info-label">رقم العقد</span>
        <span class="info-value">${c.contract_no||'_________________'}</span>
      </div>
      <div class="info-row">
        <span class="info-label">الموضوع</span>
        <span class="info-value">${c.subject||'مستند استلام مشروع'}</span>
      </div>
      ${client?`<div class="info-row">
        <span class="info-label">العميل</span>
        <span class="info-value">${client.name}</span>
      </div>
      <div class="info-row">
        <span class="info-label">رقم التواصل</span>
        <span class="info-value">${client.phone||'_________________'}</span>
      </div>`:''}
    </div>

    <!-- Main Text -->
    <div class="main-text">
      <strong>الموضوع:</strong> مستند استلام مشروع<br/><br/>
      موضوع العقد رقم <strong>${c.contract_no||'_______'}</strong>، بموجب هذا المستند يقر السيد / السيدة
      <strong> ${c.owner_name||'_______________________'} </strong>
      مالك / مالكة المشروع موضوع العقد المشار إليه أعلاه باستلامه للمشروع المرفق مع هذا المستند،
      وذلك بعد الاطلاع عليه كاملاً ومعاينته معاينة نافية للجهالة طبقاً للشروط والمواصفات
      الموجودة بالعقد حيث قام باستلام الآتي:
    </div>

    <!-- Items -->
    ${items?`<div class="items-section">
      <div class="items-title">العناصر المستلمة</div>
      <ul class="items-list">${items}</ul>
    </div>`:'<div class="items-section"><ul class="items-list"><li>___________________________</li><li>___________________________</li><li>___________________________</li></ul></div>'}

    <!-- Terms -->
    <div class="terms">
      بعد التوقيع على هذا المستند بالاستلام لا يحق للعميل طلب أي تعديل أو تغيير على المشروع أو أي من ملحقاته،
      كما تبدأ فترة الصيانة المتفق عليها في العقد من تاريخ هذا الاستلام.
    </div>

    <!-- Signature -->
    <div class="sig-section">
      <div class="sig-box">
        <div class="sig-title">مالك المشروع</div>
        <div class="sig-name-label">الإسم</div>
        <div class="sig-name-value">${c.owner_name||'_______________________'}</div>
        <div class="sig-line">التوقيع<br/>__________________________</div>
      </div>
    </div>

    <!-- Footer -->
    <div class="footer">
      <div class="footer-date">
        <div>${new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'long',year:'numeric'})}</div>
        <div>teknulugy.com</div>
      </div>
      <div class="footer-company" style="text-align:center">
        <strong style="color:#00b368">TEKNULUGY</strong> — تكنولوجي المتكاملة لتصميم وبرمجة البرمجيات الخاصة<br/>
        Ahmed Al Jaber Street, Baitak Tower, Floor 1, Office D, Kuwait City<br/>
        +965 55107000 | info@teknulugy.com
      </div>
      <div style="text-align:left;font-size:10px;color:#ccc">
        <div style="font-size:16px;font-weight:800;color:#00b368">TEKNULUGY</div>
        <div>IT Services Company</div>
      </div>
    </div>

  </div>
  </body></html>`);
  win.document.close();
  setTimeout(()=>win.print(), 800);
}

export default function ProjectClosuresPage() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [selectedClient, setSelectedClient] = useState(null);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  const { data, isLoading } = useQuery({
    queryKey:['project-closures'],
    queryFn:()=>api.get('/project-closures').then(r=>r.data),
  });
  const { data:clientData } = useQuery({
    queryKey:['clients-list'],
    queryFn:()=>getClients({limit:200}).then(r=>r.data),
  });

  const createMut = useMutation({
    mutationFn:()=>api.post('/project-closures',form),
    onSuccess:()=>{ qc.invalidateQueries(['project-closures']); setShowForm(false); setForm(EMPTY); setSelectedClient(null); toast.success('Closure document created!'); },
    onError:e=>toast.error(e.response?.data?.message||'Error'),
  });
  const updateMut = useMutation({
    mutationFn:()=>api.put('/project-closures/'+editing.id,form),
    onSuccess:()=>{ qc.invalidateQueries(['project-closures']); setEditing(null); setSelectedClient(null); toast.success('Updated!'); },
  });
  const deleteMut = useMutation({
    mutationFn:id=>api.delete('/project-closures/'+id),
    onSuccess:()=>{ qc.invalidateQueries(['project-closures']); toast.success('Deleted'); },
  });

  const closures = data?.data || [];
  const clients  = clientData?.data || [];

  const handleClientChange = (clientId) => {
    set('client_id', clientId);
    const client = clients.find(c=>String(c.id)===String(clientId));
    setSelectedClient(client||null);
    if (client) set('owner_name', client.name_ar || client.name);
  };

  const openEdit = c => {
    setEditing(c);
    setForm({ client_id:c.client_id||'', contract_no:c.contract_no||'', subject:c.subject||'', owner_name:c.owner_name||'', items:c.items||'', closure_date:c.closure_date||'', notes:c.notes||'', status:c.status });
    const client = clients.find(cl=>String(cl.id)===String(c.client_id));
    setSelectedClient(client||null);
    setShowForm(false);
  };

  return (
    <div>
      <div className="g3" style={{marginBottom:20}}>
        <div className="card-sm"><div className="stat-label">Total Documents</div><div className="stat-val">{closures.length}</div></div>
        <div className="card-sm"><div className="stat-label">Signed</div><div className="stat-val" style={{color:'var(--info)'}}>{closures.filter(c=>c.status==='Signed').length}</div></div>
        <div className="card-sm"><div className="stat-label">Closed</div><div className="stat-val" style={{color:'var(--green)'}}>{closures.filter(c=>c.status==='Closed').length}</div></div>
      </div>

      <div style={{display:'flex',justifyContent:'flex-end',marginBottom:16}}>
        <button className="btn btn-primary" onClick={()=>{setShowForm(v=>!v);setEditing(null);setForm(EMPTY);setSelectedClient(null);}}>+ New Closure Document</button>
      </div>

      {(showForm||editing) && (
        <div className="card" style={{marginBottom:16}}>
          <div className="form-section-title">{editing?'Edit Closure Document':'New Project Closure Document'}</div>
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Client</label>
              <select className="form-select" value={form.client_id} onChange={e=>handleClientChange(e.target.value)}>
                <option value="">Select client...</option>
                {clients.filter(c=>c.name_ar&&c.name_ar.trim()!=='').map(c=><option key={c.id} value={c.id}>{c.name_ar} — {c.name}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">Contract No / رقم العقد</label>
              <input className="form-input" value={form.contract_no} onChange={e=>set('contract_no',e.target.value)} placeholder="CONTRACT-001"/></div>
            <div className="form-group"><label className="form-label">Subject / الموضوع</label>
              <input className="form-input" value={form.subject} onChange={e=>set('subject',e.target.value)} placeholder="مستند استلام مشروع"/></div>
            <div className="form-group"><label className="form-label">Owner Name / اسم المالك</label>
              <input className="form-input" value={form.owner_name} onChange={e=>set('owner_name',e.target.value)} placeholder="Full name of project owner"/></div>
            <div className="form-group"><label className="form-label">Closure Date / تاريخ الاستلام</label>
              <input className="form-input" type="date" value={form.closure_date} onChange={e=>set('closure_date',e.target.value)}/></div>
            <div className="form-group"><label className="form-label">Status</label>
              <select className="form-select" value={form.status} onChange={e=>set('status',e.target.value)}>
                <option value="Draft">Draft</option>
                <option value="Signed">Signed</option>
                <option value="Closed">Closed</option>
              </select></div>
            <div className="form-group" style={{gridColumn:'1/-1'}}>
              <label className="form-label">Delivered Items / العناصر المستلمة (one per line)</label>
              <textarea className="form-textarea" style={{minHeight:120,direction:'rtl',textAlign:'right'}} value={form.items} onChange={e=>set('items',e.target.value)} placeholder="Website files&#10;Admin panel access&#10;Source code&#10;Documentation"/>
            </div>
            <div className="form-group" style={{gridColumn:'1/-1'}}><label className="form-label">Notes</label>
              <textarea className="form-textarea" value={form.notes} onChange={e=>set('notes',e.target.value)} placeholder="Additional notes..."/></div>
          </div>

          {selectedClient && (
            <div style={{background:'linear-gradient(135deg,#f0fdf7,#e8f8f1)',border:'1.5px solid #86efac',borderRadius:12,padding:16,margin:'16px 0'}}>
              <div style={{fontSize:11,fontWeight:700,color:'var(--green)',marginBottom:8}}>CLIENT INFO</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10}}>
                <div><div style={{fontSize:11,color:'var(--muted)'}}>Name</div><div style={{fontWeight:600}}>{selectedClient.name}</div></div>
                <div><div style={{fontSize:11,color:'var(--muted)'}}>الاسم بالعربي</div><div style={{fontWeight:600,direction:'rtl'}}>{selectedClient.name_ar||'--'}</div></div>
                <div><div style={{fontSize:11,color:'var(--muted)'}}>Phone</div><div style={{fontWeight:600}}>{selectedClient.phone||'--'}</div></div>
                <div><div style={{fontSize:11,color:'var(--muted)'}}>Email</div><div style={{fontWeight:600}}>{selectedClient.email||'--'}</div></div>
              </div>
            </div>
          )}

          <div style={{display:'flex',gap:8,marginTop:8}}>
            <button className="btn btn-primary" onClick={()=>editing?updateMut.mutate():createMut.mutate()}>Save Document</button>
            <button className="btn btn-outline" onClick={()=>{setShowForm(false);setEditing(null);setSelectedClient(null);}}>Cancel</button>
          </div>
        </div>
      )}

      <div className="card">
        {isLoading?<div className="loading-center"><div className="spinner"/></div>:(
          <table className="tbl">
            <thead>
              <tr><th>Client</th><th>Contract No</th><th>Subject</th><th>Owner</th><th>Date</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {closures.length===0&&<tr><td colSpan={7} className="empty-state">No closure documents found</td></tr>}
              {closures.map(c=>(
                <tr key={c.id}>
                  <td style={{fontWeight:600}}>{c.client?.name||'--'}</td>
                  <td style={{fontFamily:'monospace',fontSize:12,color:'var(--muted)'}}>{c.contract_no||'--'}</td>
                  <td style={{color:'var(--muted)',fontSize:12,maxWidth:150,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.subject||'--'}</td>
                  <td style={{fontSize:12}}>{c.owner_name||'--'}</td>
                  <td style={{color:'var(--muted)',fontSize:12}}>{c.closure_date||'--'}</td>
                  <td><span className={"badge "+(STATUS_BADGE[c.status]||'badge-muted')}>{c.status}</span></td>
                  <td>
                    <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                      <button className="btn btn-outline btn-sm" onClick={()=>openEdit(c)}>Edit</button>
                      <button className="btn btn-primary btn-sm" onClick={()=>printClosure(c,clients)} style={{display:'flex',alignItems:'center',gap:4}}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                        Print
                      </button>
                      <select className="form-select" style={{width:100,padding:'4px 8px',fontSize:11}} value={c.status}
                        onChange={e=>{ updateMut.mutate(); set('status',e.target.value); api.put('/project-closures/'+c.id,{...c,status:e.target.value}).then(()=>qc.invalidateQueries(['project-closures'])); }}>
                        <option value="Draft">Draft</option>
                        <option value="Signed">Signed</option>
                        <option value="Closed">Closed</option>
                      </select>
                      <button className="btn btn-danger btn-sm" onClick={()=>{if(window.confirm('Delete?'))deleteMut.mutate(c.id);}}>X</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
